/**
 * Fonte única da normalização de número de UC.
 *
 * `faturas.uc_codigo` é texto livre gravado pelo robô de extração: o mesmo
 * cliente aparece como '760000116', '150020301260', '1.500.203.012-60' ou
 * ' 760000116'. Casar isso com `unidades_consumidoras` por igualdade de string
 * esconde faturas da tela — foi assim que a UC 689 parou de mostrar ABR a
 * JUL/2026 (460 faturas em 162 UCs na base inteira).
 *
 * Esta função espelha exatamente a coluna gerada `uc_codigo_norm` em
 * `faturas` e `lembrete_cobranca_log` (migration 20260803130000). Se mexer numa,
 * mexa na outra.
 */
export const normalizeUcCodigo = (uc?: string | number | null): string => {
  if (typeof uc !== 'string' && typeof uc !== 'number') return '';
  return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
};

/**
 * Os números de UC que um `faturas.uc_codigo` carrega.
 *
 * Quase sempre é um só. Mas a cobrança avulsa que junta duas unidades do mesmo
 * cliente é lançada com os dois números no campo: "10015106754 / 10039677999"
 * (acordo do Eurivan), "10025066305 / 10006682110" (Super Gelo). São 37 faturas
 * assim hoje, todas AVULSO.
 *
 * Tratar essa string como um código só cria uma UC FANTASMA: ela não casa com
 * cadastro nenhum, entra na rota como uma terceira parada do mesmo cliente e é
 * cobrada de termo de adesão que a unidade de verdade já tem.
 *
 * Só desdobra quando as duas partes parecem número de UC (8 dígitos ou mais).
 * "3544/25", que é número de contrato digitado no lugar da UC, continua inteiro
 * — desdobrá-lo casaria com qualquer cadastro que começasse com 3544.
 */
const MIN_DIGITOS_UC = 8;

export const ucCodigosDaFatura = (uc?: string | number | null): string[] => {
  const inteiro = normalizeUcCodigo(uc);
  if (!inteiro.includes('/')) return inteiro ? [inteiro] : [];

  const partes = String(uc).split('/').map(normalizeUcCodigo).filter(Boolean);
  if (partes.length < 2 || partes.some(p => p.length < MIN_DIGITOS_UC)) {
    return inteiro ? [inteiro] : [];
  }
  return Array.from(new Set(partes));
};

/**
 * Chaves normalizadas de uma UC (numero_uc + outro_numero_uc), prontas para
 * `.in('uc_codigo_norm', ...)`. Deduplicado e sem vazios — uma lista vazia
 * faria o `.in` casar com nada.
 */
export const ucCodigoKeys = (
  numeroUc?: string | number | null,
  outroNumeroUc?: string | number | null
): string[] => {
  const keys = [normalizeUcCodigo(numeroUc), normalizeUcCodigo(outroNumeroUc)].filter(Boolean);
  return Array.from(new Set(keys));
};

/**
 * Expande uma lista de UCs para todos os números pelos quais a mesma unidade
 * pode estar gravada em `faturas`.
 *
 * Existe porque a mesma unidade tem dois números: o que o pessoal usa no dia a
 * dia e o código interno da distribuidora, e `faturas.uc_codigo` é gravado ora
 * com um, ora com outro. Filtrar só pelo número digitado esconde a maior parte
 * das faturas — foi o que aconteceu no levantamento das 13 geradoras, em que
 * buscar pelos números da lista achou 16 dos 60 meses que existiam.
 *
 * Devolve as chaves já normalizadas, prontas para casar com `uc_codigo_norm`.
 */
export const expandirChavesUc = (
  ucs: Iterable<string>,
  unidades: { numero_uc?: string | null; outro_numero_uc?: string | null }[]
): Set<string> => {
  const alvo = new Set(Array.from(ucs, normalizeUcCodigo).filter(Boolean));
  if (alvo.size === 0) return alvo;

  for (const u of unidades) {
    const chaves = ucCodigoKeys(u.numero_uc, u.outro_numero_uc);
    if (chaves.some(k => alvo.has(k))) {
      for (const k of chaves) alvo.add(k);
    }
  }
  return alvo;
};
